//= require personalised-config.js
//= require personalised-utils.js
// CUSTOM: personalised-bundle-cart-sync.js

(function() {
  // Ensure namespace exists
  window.personalised = window.personalised || {};
  
  // Set up a global sync state object to track sync status across the application
  window.personalised.cartSyncState = window.personalised.cartSyncState || {
    syncInProgress: false,
    syncPending: false    // PR-2.1: true while user-triggered dirty state is unresolved (debounce window through final sync completion)
  };
  
  // Operation queue system to prevent race conditions
  const operationQueue = {
    queue: [],
    processing: false,
    operationTimeout: window.personalised.config.timing.operationTimeout, // Timeout for any cart operation
    
    // Add an operation to the queue
    enqueue: function(operation) {
      return new Promise((resolve, reject) => {
        this.queue.push({
          operation,
          resolve,
          reject
        });
        window.personalised?.debug?.trace('cartSync:queueEnqueued', { queueDepth: this.queue.length });
        
        // Start processing if not already in progress
        if (!this.processing) {
          this.processNext();
        }
      });
    },
    
    // Process the next operation in the queue
    processNext: function() {
      if (this.queue.length === 0) {
        this.processing = false;
        return;
      }
      
      this.processing = true;
      const { operation, resolve, reject } = this.queue.shift();
      window.personalised?.debug?.trace('cartSync:queueProcessing', { remainingDepth: this.queue.length });
      
      // Create a timeout guard to prevent hanging operations
      let operationCompleted = false;
      const timeoutId = setTimeout(() => {
        if (!operationCompleted) {
          console.error('Cart operation timed out after ' + this.operationTimeout + 'ms');
          reject(new Error('Cart operation timed out after ' + this.operationTimeout + 'ms'));
          this.processNext(); // Continue to next operation after timeout
        }
      }, this.operationTimeout);
      
      // Execute the operation with timeout protection
      Promise.resolve()
        .then(() => operation())
        .then(result => {
          operationCompleted = true;
          clearTimeout(timeoutId);
          resolve(result);
          this.processNext(); // Continue to the next operation
        })
        .catch(error => {
          operationCompleted = true;
          clearTimeout(timeoutId);
          reject(error);
          this.processNext(); // Continue even after error
        });
    }
  };
    
  // Cart sync module
  const cartSync = {
    // Flag to track sync status
    _syncInProgress: false,
    _debounceTimer: null,
    // PR-2.1: at-most-one pending follow-up sync flag (coalescing model)
    _pendingSyncRequired: false,
    // PR-2.2: last successfully synced bundle-state fingerprint.
    // Module-level (session-scoped) — resets to null on page load, so the first
    // sync of each session always runs (covers direct navigation / stale cart).
    _lastSyncedStateHash: null,
    
    // Retry configuration (values from centralised config)
    _maxRetries: window.personalised.config.retry.maxAttempts,
    _retryDelay: window.personalised.config.retry.baseDelay,
    _lastSyncError: null,

    _applyOneOffMonthsPropertyToLineItems: function(lineItems) {
      if (!Array.isArray(lineItems) || lineItems.length === 0) return lineItems;

      const bundle = window.personalised && window.personalised.bundle;
      const isOneOff = bundle && typeof bundle.isOneOffPurchase === 'function' && bundle.isOneOffPurchase();
      if (!isOneOff) return lineItems;

      const months = Number(bundle.getOneOffMonths ? bundle.getOneOffMonths() : bundle.oneOffMonths);
      if (![4, 6].includes(months)) return lineItems;

      const ONEOFF_MONTHS_PROPERTY = window.personalised.config?.bundle?.RECHARGE_KEYS?.ONEOFF_MONTHS_PROPERTY || '_vitable_oneoff_months';
      lineItems.forEach(item => {
        item.properties = item.properties || {};
        item.properties[ONEOFF_MONTHS_PROPERTY] = String(months);
        delete item.properties.vitable_oneoff_months;
        delete item.selling_plan_id;
        delete item.selling_plan;
        delete item.sellingPlanId;
      });
      return lineItems;
    },

    _applyOneOffVariantMapToLineItems: function(lineItems) {
      // Step-2 cleanup: one-off mapping architecture is retired.
      // Keep this hook as a no-op to avoid touching downstream pipeline calls.
      return { ok: true, lineItems };
    },
    
    // Helper function to debounce callbacks (kept for legacy compatibility)
    _debounceWait: function(callback, delay = 500) {
      const self = this;
      clearTimeout(self._debounceTimer);
      self._debounceTimer = setTimeout(callback, delay);
    },

    // PR-2.2: Compute a deterministic fingerprint of the current bundle state.
    // Returns null on any error — null never suppresses sync (fails open).
    _computeStateHash: function() {
      try {
        const bundle = window.personalised.bundle;
        const frequency = window.personalised.bundle.getFrequency();
        const items = window.personalised.bundle.getItems();
        if (frequency === undefined || frequency === null || !Array.isArray(items)) return null;
        const purchaseType = bundle && typeof bundle.getPurchaseType === 'function'
          ? String(bundle.getPurchaseType() || 'subscription')
          : 'subscription';
        const oneOffMonths = bundle && typeof bundle.getOneOffMonths === 'function'
          ? Number(bundle.getOneOffMonths() || 0)
          : 0;
        const sorted = items.slice().sort(function(a, b) {
          return String(a.variantId || '').localeCompare(String(b.variantId || ''));
        });
        return JSON.stringify({
          frequency: String(frequency),
          purchaseType: purchaseType,
          oneOffMonths: [4, 6].includes(oneOffMonths) ? oneOffMonths : null,
          items: sorted.map(function(i) {
            return { variantId: String(i.variantId || ''), quantity: Number(i.quantity || 1) };
          })
        });
      } catch (e) {
        return null; // Never suppress on hash error
      }
    },

    // PR-2.2: Lightweight cart check for review-page defensive sync on fresh page load.
    // Fetches GET /cart.js and compares _rc_bundle_parent items against expected bundle state.
    // Returns true (cart is already correct, skip sync) or false (cart is stale, sync needed).
    // Always fails open (returns false) on any error so sync runs if something goes wrong.
    // Called ONLY from _executeCartRefresh when _lastSyncedStateHash === null (fresh session).
    // NOT called from _syncPipelineNow (user-interaction path — hash guard handles that).
    _isCartFreshForCurrentState: async function() {
      try {
        const items = window.personalised.bundle.getItems();
        const frequency = window.personalised.bundle.getFrequency();
        if (!Array.isArray(items) || frequency === undefined || frequency === null) return false;

        // Fetch the current Shopify cart.
        const res = await fetch(window.personalised.config.api.cart, { credentials: 'same-origin' });
        if (!res.ok) return false;
        const cart = await res.json();

        const bundleParentKey = window.personalised.config.bundle.BUNDLE_PARENT_PROPERTY_KEY;
        const BUNDLE_PARENT_PROPERTY = window.personalised.config.bundle.RECHARGE_KEYS.BUNDLE_PARENT_PROPERTY;

        // Find bundle items in cart (tagged by Recharge with _rc_bundle_parent).
        const cartBundleItems = (cart.items || []).filter(function(item) {
          return item.properties && item.properties[BUNDLE_PARENT_PROPERTY] === bundleParentKey;
        });

        // Empty bundle case: cart should have no bundle items.
        if (items.length === 0) {
          const isEmpty = cartBundleItems.length === 0;
          window.personalised?.debug?.trace('cartSync:cartFreshCheck:emptyBundle', { cartHasBundleItems: !isEmpty });
          return isEmpty;
        }

        // Build expected map: variantId → total expected quantity (item.quantity * frequencyMultiplier).
        const multiplier = window.personalised.bundle.getFrequencyMultiplier() || 1;
        const expectedMap = {};
        items.forEach(function(i) {
          const vid = String(i.variantId || '');
          if (!vid) return;
          expectedMap[vid] = (expectedMap[vid] || 0) + ((i.quantity || 1) * multiplier);
        });

        // Build actual map: variant_id → total actual quantity from cart bundle items.
        const actualMap = {};
        cartBundleItems.forEach(function(item) {
          const vid = String(item.variant_id || '');
          if (!vid) return;
          actualMap[vid] = (actualMap[vid] || 0) + (item.quantity || 0);
        });

        // Compare: same keys and same quantities.
        const expectedKeys = Object.keys(expectedMap).sort();
        const actualKeys = Object.keys(actualMap).sort();

        if (expectedKeys.length !== actualKeys.length) {
          window.personalised?.debug?.trace('cartSync:cartFreshCheck:countMismatch', { expected: expectedKeys.length, actual: actualKeys.length });
          return false;
        }
        for (let i = 0; i < expectedKeys.length; i++) {
          if (expectedKeys[i] !== actualKeys[i]) {
            window.personalised?.debug?.trace('cartSync:cartFreshCheck:variantMismatch', { expectedVariant: expectedKeys[i], actualVariant: actualKeys[i] });
            return false;
          }
          if (expectedMap[expectedKeys[i]] !== actualMap[actualKeys[i]]) {
            window.personalised?.debug?.trace('cartSync:cartFreshCheck:quantityMismatch', { variantId: expectedKeys[i], expected: expectedMap[expectedKeys[i]], actual: actualMap[actualKeys[i]] });
            return false;
          }
        }

        window.personalised?.debug?.trace('cartSync:cartFreshCheck:cartIsCorrect', { variantCount: expectedKeys.length });
        return true; // Cart already reflects current bundle state.
      } catch (e) {
        window.personalised?.debug?.trace('cartSync:cartFreshCheck:error', { message: e && e.message });
        return false; // Fail open: any error means we should sync.
      }
    },

    // PR-2.1: Outer debounce trigger — called by debouncedRefreshCartFromState.
    // Sets syncPending immediately (before the timer fires) so navigation protection
    // covers the debounce window. Resets the trailing-edge timer on each call.
    scheduleSync: function(message = null) {
      const self = this;
      // Mark dirty cycle open immediately — navigation protection starts now.
      window.personalised.cartSyncState.syncPending = true;
      window.personalised?.debug?.trace('cartSync:syncScheduled', { message });
      // Notify navigation guard so it can arm the beforeunload handler.
      if (window.personalised?.eventBus) {
        window.personalised.eventBus.emit('syncPendingStarted', {});
      }
      // Reset trailing-edge debounce timer.
      clearTimeout(self._debounceTimer);
      self._debounceTimer = setTimeout(async function() {
        window.personalised?.debug?.trace('cartSync:outerDebounceFired', { queueProcessing: operationQueue.processing });
        if (operationQueue.processing) {
          // Coalesce: a sync is already running; record that a follow-up is needed.
          // Do NOT enqueue another stale rebuild — at most one pending follow-up.
          self._pendingSyncRequired = true;
          window.personalised?.debug?.trace('cartSync:coalescedWhileActive', {});
        } else {
          await self._startSyncCycle(message);
        }
      }, window.personalised.config.timing.cartSyncDebounce);
    },

    // PR-2.1: Lifecycle coordinator for one sync cycle.
    // Emits syncStarted / syncCompleted / syncFailed and manages syncPending + coalescing.
    _startSyncCycle: async function(message) {
      const self = this;
      const utils = window.personalised.utils;

      self._syncInProgress = true;
      if (utils && typeof utils.startSync === 'function') utils.startSync();

      window.personalised?.debug?.trace('cartSync:syncCycleStarting', {});
      if (window.personalised?.eventBus) {
        window.personalised.eventBus.emit('syncStarted', { message });
      }

      let succeeded = false;
      try {
        succeeded = await operationQueue.enqueue(async () => self._syncPipelineNow(message));
      } catch (err) {
        // Queue timeout or unexpected rejection
        console.error('[CartSync] Unexpected queue error:', err);
        succeeded = false;
      }

      self._syncInProgress = false;
      if (utils && typeof utils.completeSync === 'function') utils.completeSync();

      if (succeeded === 'skip') {
        // Clean guard exit (e.g. edit mode entered just before fire) — release quietly.
        window.personalised?.debug?.trace('cartSync:syncCycleSkipped', {});
        self._pendingSyncRequired = false;
        window.personalised.cartSyncState.syncPending = false;
        return;
      }

      if (succeeded !== false) {
        // Success path.
        window.personalised?.debug?.trace('cartSync:syncCycleCompleted', { followUpRequired: self._pendingSyncRequired });
        if (self._pendingSyncRequired) {
          // Coalescing follow-up: consume the token and run one more sync.
          // Do NOT emit syncCompleted here — the follow-up cycle will emit it
          // when it finishes, preventing a brief idle-state UI flicker.
          // syncPending remains true — the follow-up cycle will clear it.
          self._pendingSyncRequired = false;
          await self._startSyncCycle(message);
        } else {
          // All-clear: no further work required. Release dirty cycle.
          // syncPending must be cleared BEFORE emitting syncCompleted because the eventBus
          // is synchronous — handlers run inside emit(), so any handler that checks
          // syncPending (e.g. the navigation guard release condition) must see it false.
          window.personalised.cartSyncState.syncPending = false;
          if (window.personalised?.eventBus) {
            window.personalised.eventBus.emit('syncCompleted', { message });
          }
        }
      } else {
        // Failure path: clear the full dirty cycle and release navigation.
        window.personalised?.debug?.trace('cartSync:syncCycleFailed', {});
        self._pendingSyncRequired = false;
        window.personalised.cartSyncState.syncPending = false;
        if (window.personalised?.eventBus) {
          window.personalised.eventBus.emit('syncFailed', {
            error: new Error('Cart sync failed'),
            message: 'Failed to update your personalized bundle in cart'
          });
        }
      }
    },

    // PR-2.1: Full synchronous pipeline executed at debounce-fire / follow-up time.
    // Reads bundle state lazily (at execution time, NOT at user-event time).
    // Returns 'skip' for clean guard exits, true for success, false for failure.
    // Does NOT emit lifecycle events — _startSyncCycle owns those.
    _syncPipelineNow: async function(message) {
      window.personalised?.debug?.time('cartSync:syncPipeline');
      window.personalised?.debug?.trace('cartSync:syncPipelineStart', {});

      // Guard: bundle loaded from Recharge — do not overwrite.
      // PR-4.3: window.personalised._loadedFromRecharge is never set in live code; the real flag
      // is in metadata. Edit-mode guard below already covers this scenario.
      if (window.personalised.storageManager?.getMetadata('loadedFromRecharge')) {
        window.personalised?.debug?.trace('cartSync:pipelineSkipped', { reason: 'loadedFromRecharge' });
        window.personalised?.debug?.timeEnd('cartSync:syncPipeline');
        return 'skip';
      }
      // Guard: initialization in progress.
      // PR-4.3: window.personalised._initializationInProgress is never set (the real lock is
      // IIFE-private inside personalised-bundle-state.js). This guard is a known dead-code gap.
      // The edit-mode guard below provides the necessary protection for the init-time scenario.
      if (window.personalised?._initializationInProgress) {
        window.personalised?.debug?.trace('cartSync:pipelineSkipped', { reason: 'initializationInProgress' });
        window.personalised?.debug?.timeEnd('cartSync:syncPipeline');
        return 'skip';
      }
      // Guard: edit mode — cart sync is not used in edit mode.
      if (window.personalised.utils.getMode() === window.personalised.config.modes.EDIT) {
        window.personalised?.debug?.trace('cartSync:pipelineSkipped', { reason: 'editMode' });
        window.personalised?.debug?.timeEnd('cartSync:syncPipeline');
        return 'skip';
      }
      // Guard: bundle must be loaded.
      if (!window.personalised.validation.validateBundleLoaded()) {
        window.personalised?.debug?.trace('cartSync:pipelineSkipped', { reason: 'bundleNotLoaded' });
        console.error('[CartSync] Bundle state not loaded');
        window.personalised?.debug?.timeEnd('cartSync:syncPipeline');
        return false;
      }

      // PR-2.2: Skip sync if state is identical to last successfully synced state.
      // Computed at execution time (LAZY) — handles coalesced follow-ups that turn out to be no-ops.
      // Returns true (not 'skip') so _startSyncCycle emits syncCompleted and the navigation guard
      // releases syncActive correctly. ('skip' is reserved for capability-based guards that prevent
      // sync from running in unsafe states; hash suppression is a no-op success, not a guard exit.)
      const syncedHash = this._computeStateHash();
      if (syncedHash !== null && syncedHash === this._lastSyncedStateHash) {
        window.personalised?.debug?.trace('cartSync:skipped:stateUnchanged', { hash: syncedHash });
        window.personalised?.debug?.timeEnd('cartSync:syncPipeline');
        return true; // No-op success: cart already reflects current bundle state
      }

      // Empty bundle: clear bundle items from cart.
      if (!window.personalised.validation.bundleHasItems()) {
        window.personalised?.debug?.trace('cartSync:pipelineEmptyBundle', {});
        const result = await this._performRebuildInCart([]);
        // PR-2.2: Store hash on success so subsequent no-ops are suppressed.
        if (result !== false && syncedHash !== null) {
          this._lastSyncedStateHash = syncedHash;
          window.personalised?.debug?.trace('cartSync:hashStored', { hash: syncedHash });
        }
        // PR-2.4: Write timestamp + clear modified flag so review-page skips defensive sync.
        if (result !== false) {
          window.personalised.storageManager?.setLastSyncTimestamp();
          window.personalised.storageManager?.clearModifiedFlag();
          window.personalised?.debug?.trace('cartSync:syncTimestampStored', { path: 'emptyBundle' });
        }
        window.personalised?.debug?.timeEnd('cartSync:syncPipeline');
        return result;
      }

      // Build payload from current bundle state (LAZY — reads state NOW).
      window.personalised?.debug?.time('cartSync:buildPayload');
      const payload = window.personalised.utils.getBundlePayload();
      window.personalised?.debug?.timeEnd('cartSync:buildPayload');
      const bundleParentKey = window.personalised.config.bundle.BUNDLE_PARENT_PROPERTY_KEY;

      // Get line items from Recharge SDK (LAZY — runs at debounce-fire / follow-up time).
      let lineItems;
      try {
        window.personalised?.debug?.time('cartSync:waitForRechargeSDK');
        await window.personalised.utils.waitForRechargeSDK();
        window.personalised?.debug?.timeEnd('cartSync:waitForRechargeSDK');

        if (!window.recharge?.bundle?.getDynamicBundleItems) {
          throw new Error('Recharge bundle SDK method not available');
        }

        window.personalised?.debug?.time('cartSync:getDynamicBundleItems');
        lineItems = await window.recharge.bundle.getDynamicBundleItems(payload, bundleParentKey);
        window.personalised?.debug?.timeEnd('cartSync:getDynamicBundleItems');
        window.personalised?.debug?.trace('cartSync:lineItemsResolved', { count: Array.isArray(lineItems) ? lineItems.length : 0 });

        if (!Array.isArray(lineItems)) {
          throw new Error('Invalid line items response from Recharge SDK');
        }

        lineItems = this._applyOneOffMonthsPropertyToLineItems(lineItems);
      } catch (sdkErr) {
        window.personalised?.debug?.timeEnd('cartSync:waitForRechargeSDK');
        window.personalised?.debug?.timeEnd('cartSync:getDynamicBundleItems');
        console.error('[CartSync] Recharge SDK error:', sdkErr);
        window.personalised?.debug?.timeEnd('cartSync:syncPipeline');
        return false;
      }

      // Execute rebuild with one automatic retry.
      let result = await this._performRebuildInCart(lineItems);
      const canRetry = !(this._lastSyncError && this._lastSyncError.nonRetryable === true);
      if (result === false && canRetry) {
        window.personalised?.debug?.trace('cartSync:pipelineRetrying', {});
        console.log('[CartSync] Retrying rebuild once after failure...');
        await new Promise(r => setTimeout(r, window.personalised.config.retry.baseDelay));
        result = await this._performRebuildInCart(lineItems);
      }

      // PR-2.2: Store hash on success.
      if (result !== false && syncedHash !== null) {
        this._lastSyncedStateHash = syncedHash;
        window.personalised?.debug?.trace('cartSync:hashStored', { hash: syncedHash });
      }
      // PR-2.4: Write timestamp + clear modified flag so review-page skips defensive sync.
      if (result !== false) {
        window.personalised.storageManager?.setLastSyncTimestamp();
        window.personalised.storageManager?.clearModifiedFlag();
        window.personalised?.debug?.trace('cartSync:syncTimestampStored', { path: 'fullSync' });
      }
      window.personalised?.debug?.timeEnd('cartSync:syncPipeline');
      return result;
    },
    
    // Helper function to create a timeout promise
    _timeout: function(ms) {
      return new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Cart sync operation timed out after ' + ms + 'ms')), ms)
      );
    },
    
    // Retry fetch with exponential backoff
    _retryFetch: async function(url, options = {}, retries = this._maxRetries) {
      try {
        const response = await fetch(url, options);
        
        // Check if the response is ok (status in the range 200-299)
        if (!response.ok) {
          const errorData = await response.text();
          const error = new Error(`HTTP error ${response.status}: ${errorData}`);
          error.status = response.status;
          error.url = url;
          // Do not retry validation/business errors like 422.
          error.nonRetryable = [400, 401, 403, 404, 422].includes(response.status);
          throw error;
        }
        
        return response;
      } catch (error) {
        if (error?.nonRetryable === true) {
          throw error;
        }
        if (retries === 0) {
          console.error(`Failed fetch to ${url} after multiple retries:`, error);
          throw error;
        }
        
        // Calculate backoff delay with some randomness to prevent synchronized retries
        const delay = this._retryDelay * Math.pow(2, this._maxRetries - retries) + (Math.random() * 200);
        console.warn(`Fetch to ${url} failed. Retrying in ${Math.round(delay)}ms... (${retries} retries left)`, error);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Retry with one fewer retry attempt
        return this._retryFetch(url, options, retries - 1);
      }
    },
    
    // Core cart sync operation — executed via the operation queue
    _performRebuildInCart: async function(lineItems) {
      // Set sync flag
      window.personalised.cartSyncState.syncInProgress = true;
      this._lastSyncError = null;
      window.personalised?.debug?.time('cartSync:rebuildInCart');
      window.personalised?.debug?.trace('cartSync:rebuildStart', { lineItemCount: lineItems ? lineItems.length : 0 });
      let syncRequestCount = 0;
      
      try {
        // Define the cart sync operation as an async function
        // Get current cart with retry
        let cart;
        window.personalised?.debug?.time('cartSync:cartFetch');
        try {
          const res = await this._retryFetch(window.personalised.config.api.cart);
          cart = await res.json();
          syncRequestCount++;
          window.personalised?.debug?.timeEnd('cartSync:cartFetch');
        } catch (error) {
          window.personalised?.debug?.timeEnd('cartSync:cartFetch');
          console.error('Failed to fetch cart after multiple retries:', error);
          throw new Error('Failed to load cart data. Please refresh the page and try again.');
        }
        
        // Find ALL personalised-bundle line items
        const bundleParentKey = window.personalised.config.bundle.BUNDLE_PARENT_PROPERTY_KEY;
        // Use centralized config for Recharge property keys
        const BUNDLE_PARENT_PROPERTY = window.personalised.config.bundle.RECHARGE_KEYS.BUNDLE_PARENT_PROPERTY;
        const bundleItems = cart.items.filter(
          item =>
            item.properties &&
            item.properties[BUNDLE_PARENT_PROPERTY] === bundleParentKey
        );
        window.personalised?.debug?.trace('cartSync:bundleItemsFound', { count: bundleItems.length });

        // Collect section IDs using FoxTheme-native pattern (cart:grouped-sections event)
        // Components push their raw data-section-id values into this array — same format that
        // CartItems.onCartUpdate expects as keys in event.cart.sections[sectionId]
        const sectionsToRequest = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToRequest } })
        );
        window.personalised?.debug?.trace('cartSync:sectionIdsCollected', { count: sectionsToRequest.length, ids: sectionsToRequest });

        let updateData = null;
        let addData = null;

        // One-off preflight: remap to one-time purchasable variants when configured.
        // Fail fast with a descriptive message instead of waiting for cart/add 422.
        const mappedResult = this._applyOneOffVariantMapToLineItems(lineItems || []);
        if (!mappedResult.ok) {
          this._lastSyncError = mappedResult.error;
          throw mappedResult.error;
        }
        lineItems = mappedResult.lineItems;

        // Batch all removals into a single /cart/update.js request
        if (bundleItems.length > 0) {
          const updates = {};
          for (const bundle of bundleItems) {
            updates[bundle.key] = 0;
          }
          
          window.personalised?.debug?.time('cartSync:cartUpdate');
          try {
            const frequency = window.personalised.bundle.getFrequency();
            const hasNonPersonalised = cart.items.some(item =>
              !item.properties || item.properties['_rc_bundle_parent'] !== 'personalised-pack'
            );
            const updateRes = await this._retryFetch(window.personalised.config.api.cartUpdate, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                updates,
                attributes: {
                  frequency,
                  hasNonPersonalised: hasNonPersonalised ? 'true' : 'false'
                },
                sections: sectionsToRequest
              })
            });
            updateData = await updateRes.json();
            syncRequestCount++;
            window.personalised?.debug?.timeEnd('cartSync:cartUpdate');
            window.personalised?.debug?.trace('cartSync:itemsRemoved', { count: bundleItems.length });
          } catch (error) {
            window.personalised?.debug?.timeEnd('cartSync:cartUpdate');
            console.error('Failed to remove bundle items from cart after multiple retries:', error);
            throw new Error('Failed to update your bundle. Please try again.');
          }
        }

        // Add new items in a single request
        if (lineItems && lineItems.length > 0) {
          window.personalised?.debug?.time('cartSync:cartAdd');
          try {
            const addRes = await this._retryFetch(window.personalised.config.api.cartAdd, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: lineItems, sections: sectionsToRequest })
            });
            addData = await addRes.json();
            syncRequestCount++;
            window.personalised?.debug?.timeEnd('cartSync:cartAdd');
            window.personalised?.debug?.trace('cartSync:itemsAdded', { count: lineItems.length });
          } catch (error) {
            window.personalised?.debug?.timeEnd('cartSync:cartAdd');
            this._lastSyncError = error;
            console.error('Failed to add bundle items to cart after multiple retries:', error);
            throw new Error('Failed to add products to your bundle. Please try again.');
          }
        }
          
        // Notify of successful sync
        if (window.personalised && window.personalised.eventBus) {
          window.personalised.eventBus.emit('syncSucceeded', { 
            message: 'Your personalized bundle has been updated' 
          });
        }

        // Publish cart update to FoxTheme pubsub using section HTML from add/update responses.
        // Section keys are raw data-section-id values collected via cart:grouped-sections, matching
        // what CartItems.onCartUpdate expects in event.cart.sections[sectionId].
        async function fetchCartJson() {
          const response = await fetch('/cart.js', { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Failed to fetch cart JSON');
          return await response.json();
        }

        async function publishCartUpdateToThemePubSub() {
          try {
            let cartData;

            if (addData) {
              // Non-empty add path: fetch fresh cart JSON for full state,
              // attach sections from the add response (keyed by raw section IDs).
              window.personalised?.debug?.time('cartSync:cartFetch2');
              cartData = await fetchCartJson();
              syncRequestCount++;
              window.personalised?.debug?.timeEnd('cartSync:cartFetch2');
              cartData.sections = addData.sections || {};
            } else if (updateData) {
              // Update-only path (bundle cleared, no new items): /cart/update.js returns
              // the full cart state — use it directly to avoid a redundant GET /cart.js.
              cartData = updateData;
              cartData.sections = cartData.sections || {};
              window.personalised?.debug?.trace('cartSync:cartFetch2Saved', { reason: 'update-only path' });
            } else {
              // Fallback: neither update nor add ran (e.g. empty bundle, cart already clear).
              cartData = await fetchCartJson();
              syncRequestCount++;
              cartData.sections = {}; // No section HTML available; CartItems.onCartUpdate needs sections to be an object, not undefined
            }

            if (window.FoxTheme?.pubsub?.publish) {
              window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                cart: cartData,
                skipFocusHandling: true
              });
            }
          } catch (err) {
            if (window.personalised && window.personalised.debugMode) {
              console.warn('[Personalised Bundle] Failed to publish cart update with sections:', err);
            }
          }
        }

        window.personalised?.debug?.time('cartSync:pubsubPublish');
        await publishCartUpdateToThemePubSub();
        window.personalised?.debug?.timeEnd('cartSync:pubsubPublish');
        window.personalised?.debug?.trace('cartSync:rebuildComplete', { bundleItemsRemoved: bundleItems.length, lineItemsAdded: (lineItems && lineItems.length) || 0, requestCount: syncRequestCount });
        
        return true;
      } catch (err) {
        this._lastSyncError = this._lastSyncError || err;
        // Log errors but don't break the UI
        console.error('❌ Error in bundle cart sync:', err);
        return false;
      } finally {
        window.personalised.cartSyncState.syncInProgress = false;
        window.personalised?.debug?.timeEnd('cartSync:rebuildInCart');
      }
    },
    
    // Wrapped rebuild function that includes one retry.
    // PR-2.1: emits syncStarted / syncCompleted / syncFailed lifecycle events so the
    // navigation guard correctly tracks immediate (non-debounced) sync operations.
    // Does NOT set syncPending — immediate paths are outside the dirty-cycle semantics.
    rebuildInCart: async function(lineItems) {
      const utils = window.personalised.utils;
      this._syncInProgress = true;
      if (utils && typeof utils.startSync === 'function') utils.startSync();

      window.personalised?.debug?.trace('cartSync:immediateRebuildStarted', { lineItemCount: lineItems ? lineItems.length : 0 });
      if (window.personalised?.eventBus) {
        window.personalised.eventBus.emit('syncStarted', { message: null });
      }

      let result;
      try {
        result = await this._performRebuildInCart(lineItems);
        if (result === false) {
          console.log('Cart sync failed, retrying once in 1 second...');
          await new Promise(resolve => setTimeout(resolve, window.personalised.config.retry.baseDelay));
          result = await this._performRebuildInCart(lineItems);
        }
      } catch (err) {
        console.error('Cart sync failed after retry:', err);
        result = false;
      }

      this._syncInProgress = false;
      if (utils && typeof utils.completeSync === 'function') utils.completeSync();

      if (result !== false) {
        if (window.personalised?.eventBus) {
          window.personalised.eventBus.emit('syncCompleted', { message: null });
        }
      } else {
        if (window.personalised?.eventBus) {
          window.personalised.eventBus.emit('syncFailed', {
            error: this._lastSyncError || new Error('Cart sync failed'),
            message: (this._lastSyncError && this._lastSyncError.message) || 'Failed to update your personalized bundle in cart'
          });
        }
      }

      return result;
    },
    
    // debouncedRebuildInCart is removed in PR-2.1.
    // The outer debounce now lives in scheduleSync() above.
    // The inner debounce that used to gate only the Shopify API calls was the wrong layer.
    // Any residual callers of debouncedRebuildInCart should be updated to use scheduleSync.
    debouncedRebuildInCart: function(lineItems, message = null) {
      // Deprecated: routes to scheduleSync for any residual callers.
      window.personalised?.debug?.trace('cartSync:debouncedRebuildInCartDeprecated', {});
      console.warn('[CartSync] debouncedRebuildInCart is deprecated; use scheduleSync instead.');
      this.scheduleSync(message);
    }
  };
  
  // Assign to single namespace
  window.personalised.cartSync = cartSync;
})();

// NOTE: All event handling for [data-add-to-pack] is now delegated in personalised-bundle-manager.js
// Use data-* attributes for any future selectors or event logic
